<script setup lang="ts">
import CardClosed from '~/components/pages/results/Header/CardClosed.vue'
import CardOpen from '~/components/pages/results/Header/CardOpen.vue'
import BackgroundBanner from '~/components/effects/BackgroundBanner.vue'
import ScanTicketBanner from '~/components/pages/results/Header/ScanTicketBanner.vue'
import { useResultStore } from '~/store/result.js'
import { getResultsParamsState } from '~/composables/useResultsParams'
import { getNumbers, toThreeDecimals, filterOnlyNumbersByGroup } from '~/utils/numbers.js'
import { countries, IDS_LOTTERIES_CLASSIC, IDS_LOTTERIES_IS_NOT_MULTIPLE_BETS } from '~/utils/constants/home.js'
import { trackUserLocation, trackUserSelectedGame, trackRandomPlay, trackLuckTesting } from '~/tracking/homeEvents'
import { trackLotterySelected } from '~/tracking/scan/scanEmptyEvents'
import { fetchLotteries, fetchLotterySelect, fetchRulesLotterySelect } from '~/utils/playLottery.js'
import { useSubscriberStore } from '~/store/subscriber'
import { combinations } from '~/utils/constants/combinations'
import { trackMultipleBetsToggle, trackMultipleBetsStarted } from '~/tracking/multipleBetsEvents'
import { ID_KENO_FR, ID_EURO_MILLIONS } from '~/utils/constants/idsLotteries'

defineOptions({
	name: 'ResultHero',
})

interface QueryParams {
	lottery_id: number | undefined
	regular_balls?: any
	extra_balls?: any
	country_id: string | number | undefined
	page_number: number
	first_number?: any
	second_number?: any
	third_number?: any
	fourth_number?: any
	fifth_number?: any
}

interface Lottery {
	id: number
	name: string
	slug: string
	image: string
}
type Item = string | number | null
type ItemArray = Item[]
interface NumbersBlock {
	first: ItemArray
	last?: ItemArray | null | undefined
}

const router = useRouter()
const route = useRoute()
interface User {
	id?: number | string
	name?: string
	email?: string
}

const resultStore = useResultStore() as {
	user: User | null
	lottery: any
	country: any
	numbers: any
	is_random: boolean
	isCompletedActiveAlarms: boolean
	SET_STATE_RESULT: (payload: any) => void
	isMutipleBets: boolean
	amount?: number | null
}

const { t: $t } = useI18n()
const subscriberStore = useSubscriberStore()

const initialBlock = {
	first: [],
	last: [],
}

const isOpen = ref<boolean>(false)
const country = ref<any>(null)
const lotterySelect = ref<number | null>(null)
const cardOpenRef = ref<{ codeLotteryRef: { setRandom: () => Promise<void> } } | null>(null)
const lotteryNumbers = ref<NumbersBlock | any>({ ...initialBlock })
const isMobile = ref(false)
const loaderSetRandomNumbers = ref(false)
const amount = ref(1)
const shouldTrackLotterySelected = ref(!getResultsParamsState(route.query).hasSufficientParams)

const getStateMultipleBets = computed(() => {
	return resultStore.isMutipleBets
})

const isShowMultipleBets = computed(() => {
	if (lotterySelect.value === null) return false
	if (IDS_LOTTERIES_IS_NOT_MULTIPLE_BETS.includes(lotterySelect.value)) return false
	return true
})

const getLotteries = computed(() => {
	const lotteries = fetchLotteries(country.value)
	return lotteries.map((lottery: Lottery) => ({
		id: lottery.id,
		label: lottery.id === ID_EURO_MILLIONS ? $t(lottery.name) : lottery.name,
	}))
})
const getLotterySelect = computed<Lottery | null>(() => fetchLotterySelect(lotterySelect.value))
const getLotterySelectItem = computed(() => {
	if (lotterySelect.value === null) return null
	const lottery = fetchLotterySelect(lotterySelect.value)
	if (lottery === null) return null
	return {
		id: lottery.id,
		label: lottery.id === ID_EURO_MILLIONS ? $t(lottery.name) : lottery.name,
	}
})

const getRulesLotterySelect = computed(() => {
	if (getLotterySelect.value === null) return null
	return fetchRulesLotterySelect(getLotterySelect.value, resultStore.isMutipleBets)
})

const getRulesLotterySelectStandard = computed(() => {
	if (getLotterySelect.value === null) return null
	return fetchRulesLotterySelect(getLotterySelect.value)
})

const getRulesLotterySelectMultiple = computed(() => {
	if (getLotterySelect.value === null) return null
	return fetchRulesLotterySelect(getLotterySelect.value, true)
})

const isDisabledBtn = computed(() => {
	if (!lotteryNumbers.value) return true
	if (loaderSetRandomNumbers.value) return true
	if (getRulesLotterySelect.value === null) return true
	const { min_count_active, first_block, last_block, id } = getRulesLotterySelect.value
	const { first, last } = lotteryNumbers.value
	/* Multiple Checks */
	if (resultStore.isMutipleBets === true) {
		const firstSelected = lotteryNumbers.value.first.filter((n: Item) => n !== null).length
		const lastSelected = lotteryNumbers.value.last
			? lotteryNumbers.value.last.filter((n: Item) => n !== null).length
			: 0

		const countAllBalls = firstSelected + lastSelected
		const firstCondition = firstSelected >= first_block.min_select

		if (lotteryNumbers.value.last === null) {
			return !(countAllBalls >= min_count_active && firstCondition)
		}

		const lastCondition = lastSelected >= last_block.min_select

		return !(countAllBalls >= min_count_active && firstCondition && lastCondition)
	}

	/* Standar keno */
	if (id === ID_KENO_FR) {
		const firstCount = first.filter((num: string | null) => num !== null).length
		return !(firstCount >= first_block.min_select)
	}

	/* Standar */
	const firstBlock = first.length > 0 ? first.some((num: Item) => num === '' || num === null) : true
	const lastBlock =
		last !== null ? (last.length > 0 ? last.some((num: Item) => num === '' || num === null) : true) : false

	if (IDS_LOTTERIES_CLASSIC.includes(getRulesLotterySelect.value.id)) {
		return firstBlock
	}

	return firstBlock || lastBlock
})

const setPropertysLottery = () => {
	if (resultStore.country !== null) {
		country.value = { ...resultStore.country }
	}

	if (resultStore?.lottery) {
		lotterySelect.value = typeof resultStore?.lottery === 'number' ? resultStore?.lottery : resultStore?.lottery.id
	}

	if (resultStore?.numbers !== null) {
		lotteryNumbers.value = { ...resultStore?.numbers }
	}

	if (resultStore && resultStore.amount != null) {
		amount.value = resultStore.amount
	}

	resultStore.SET_STATE_RESULT({
		type: 'combinations',
		value: getCombinations.value,
	})

	verificatedAlertActive()
}

const updateQuerySearch = async (object: any, pagination = false) => {
	const query = { ...object }
	if (!pagination) {
		query.page_number = '1'
	}
	await router.replace({ query })
}

const setQueryParams = (): QueryParams => {
	const { first, last } = lotteryNumbers.value
	const regularBalls = first.filter((n: string | null) => n !== null)

	if (getLotterySelect.value && IDS_LOTTERIES_CLASSIC.includes(getLotterySelect.value?.id)) {
		const [first_number, second_number, third_number, fourth_number, fifth_number] = first ?? []
		return {
			country_id: country.value?.id,
			lottery_id: getLotterySelect.value?.id,
			first_number,
			second_number,
			third_number,
			fourth_number,
			fifth_number,
			page_number: 1,
		}
	}

	const params: QueryParams & Record<string, any> = {
		country_id: country.value?.id,
		lottery_id: getLotterySelect.value?.id,
		regular_balls: regularBalls,
		page_number: 1,
	}
	if (last !== null) {
		params.extra_balls = last.filter((n: string | null) => n !== null)
	}
	if (resultStore.isMutipleBets == true) {
		params.is_multiple_bets = true
	}

	if (ID_KENO_FR === getLotterySelect.value?.id) {
		params.number_of_balls = regularBalls.length
		params.amount = amount.value
	}

	return params
}

const getCombinations = computed(() => {
	if (!lotteryNumbers.value || !getRulesLotterySelect.value) return 0
	const { first, last = [] } = lotteryNumbers.value
	const { first_block, last_block } = getRulesLotterySelect.value

	const min = first_block.min_select ?? 0
	const counter = first.filter((n: number | null) => n !== null).length
	const firstCombination = combinations(counter, min)

	if (last === null) {
		return toThreeDecimals(firstCombination)
	}

	const minLast = last_block.min_select ?? 0
	const counterLast = last.filter((n: number | null) => n !== null).length
	return toThreeDecimals(firstCombination * combinations(counterLast, minLast))
})

const showResult = async () => {
	if (isDisabledBtn.value) return
	await nextTick()

	resultStore.SET_STATE_RESULT({
		type: 'numbers',
		value: filterOnlyNumbersByGroup(lotteryNumbers.value),
	})

	resultStore.SET_STATE_RESULT({
		type: 'current_numbers',
		value: filterOnlyNumbersByGroup(lotteryNumbers.value),
	})

	if (shouldTrackLotterySelected.value && country.value?.id && getLotterySelect.value?.id) {
		trackLotterySelected({ lottery_id: getLotterySelect.value.id })
		shouldTrackLotterySelected.value = false
	}

	const query = setQueryParams()
	await updateQuerySearch(query)

	if (resultStore.isMutipleBets == true) {
		resultStore.SET_STATE_RESULT({
			type: 'combinations',
			value: getCombinations.value,
		})

		trackMultipleBetsStarted({
			lottery_id: lotterySelect.value,
			combination_count: getCombinations.value,
			numbers: getNumbers(lotteryNumbers.value)?.join(','),
		})
		isOpen.value = false
		return
	}

	trackLuckTesting({
		user_id: resultStore.user?.id,
		country: country.value?.label,
		game_name: getLotterySelect.value?.name,
		is_random: resultStore.is_random,
		numbers: getNumbers(lotteryNumbers.value)?.join(','),
	})
	isOpen.value = false
	const el = document.querySelector('#heroResults')
	el?.scrollIntoView({ behavior: 'smooth' })
}

const updateNumbers = (value: any | null) => {
	lotteryNumbers.value = value

	resultStore.SET_STATE_RESULT({
		type: 'current_numbers',
		value: value,
	})
}

const random = async () => {
	loaderSetRandomNumbers.value = true
	if (cardOpenRef.value === null || cardOpenRef.value?.codeLotteryRef == null) {
		loaderSetRandomNumbers.value = false
		return
	}

	await cardOpenRef.value.codeLotteryRef.setRandom()
	loaderSetRandomNumbers.value = false
	resultStore.SET_STATE_RESULT({
		type: 'is_random',
		value: true,
	})

	resultStore.SET_STATE_RESULT({
		type: 'numbers',
		value: filterOnlyNumbersByGroup(lotteryNumbers.value),
	})

	resultStore.SET_STATE_RESULT({
		type: 'current_numbers',
		value: filterOnlyNumbersByGroup(lotteryNumbers.value),
	})

	trackRandomPlay({
		user_id: resultStore.user?.id,
		country: country.value?.label,
		game_name: getLotterySelect.value?.name,
		random_numbers: getNumbers(lotteryNumbers.value),
	})
}

const updateBlock = (property: 'first' | 'last', value: string, mode = null) => {
	updateBlockNumbers({
		property: property,
		blockNumbers: lotteryNumbers.value,
		rules: getRulesLotterySelect.value,
		ruleProperty: value,
		mode: mode,
		multipleBets: isShowMultipleBets.value === true ? resultStore.isMutipleBets : false,
	})
}

const setNumbers = (mode = null) => {
	updateBlock('first', 'first_block', mode)
	updateBlock('last', 'last_block', mode)
}

const verificatedAlertActive = async () => {
	if (resultStore.user === null || !resultStore.user?.email) return
	if (getLotterySelect.value === null) {
		if (!resultStore.isCompletedActiveAlarms) return
		resultStore.SET_STATE_RESULT({
			type: 'isCompletedActiveAlarms',
			value: false,
		})
		return
	}

	const validateContent = {
		email: resultStore.user?.email,
		lottery_id: getLotterySelect.value.id,
		country_id: country?.value.id,
	}
	const response = await subscriberStore.validateSubscriber(validateContent)
	if (response.success) {
		resultStore.SET_STATE_RESULT({
			type: 'isCompletedActiveAlarms',
			value: true,
		})
		return
	}
	resultStore.SET_STATE_RESULT({
		type: 'isCompletedActiveAlarms',
		value: false,
	})
}

const updateCountry = (value: any) => {
	const { id: countryId, label: countryLabel, ...allItems } = value
	country.value = { id: countryId, label: $t(countryLabel), ...allItems }

	resultStore.SET_STATE_RESULT({
		type: 'country',
		value: country.value,
	})

	if (getLotteries.value.length > 1) {
		lotterySelect.value = null
		lotteryNumbers.value = null

		resultStore.SET_STATE_RESULT({
			type: 'lottery',
			value: null,
		})
	} else {
		const { id } = getLotteries.value[0]
		lotterySelect.value = id
		setNumbers()

		const { label } = getLotteries.value[0]
		trackUserSelectedGame({ game_name: label, user_id: resultStore.user?.id })

		resultStore.SET_STATE_RESULT({
			type: 'lottery',
			value: getLotterySelect.value,
		})
	}
	trackUserLocation({ country: $t(countryLabel), user_id: resultStore.user?.id })
	verificatedAlertActive()
}

const updateLotteryIfClosed = (value: any) => {
	updateLottery(value)
	toggleOpen()
}

const updateLottery = (value: any) => {
	lotterySelect.value = value?.id
	setNumbers()
	const lotteries = fetchLotteries(country.value)
	const findLottery = lotteries.find((lottery) => lottery.id === value.id)
	if (IDS_LOTTERIES_CLASSIC.includes(value?.id)) {
		resultStore.SET_STATE_RESULT({ type: 'isMutipleBets', value: false })
	}

	if (!findLottery) return
	resultStore.SET_STATE_RESULT({
		type: 'lottery',
		value: findLottery,
	})
	trackUserSelectedGame({ game_name: findLottery.name, user_id: resultStore.user?.id })
	verificatedAlertActive()
}

const sendEvent = () => {
	const { first, last } = lotteryNumbers.value
	const contentEvent = {
		lottery_id: lotterySelect.value,
		lottery_name: getLotterySelect.value?.name,
		mode: !getStateMultipleBets?.value ? 'on' : 'off',
		selected_count_main: first.filter((n: number | null) => n !== null).length,
		selected_count_special: last === null ? 0 : last.filter((n: number | null) => n !== null).length,
		numbers: getOnlyNumbers(lotteryNumbers.value)?.join(','),
	}
	trackMultipleBetsToggle(contentEvent)
}

const updateNumbersReduced = () => {
	const { first_block, last_block, id } = getRulesLotterySelectStandard.value
	const { first, last } = lotteryNumbers.value || { first: [], last: null }

	const countFirstBlock = lotteryNumbers.value?.first?.length || 0
	const countLastBlock = lotteryNumbers.value?.last?.length || 0

	const conditionalTotal = id === ID_KENO_FR ? first_block.min_select : first_block.total

	if (lotteryNumbers.value && countFirstBlock > conditionalTotal) {
		lotteryNumbers.value.first = first.slice(0, conditionalTotal)
	}
	if (last === null && last_block === null) return
	if (lotteryNumbers.value && countLastBlock > last_block.total) {
		lotteryNumbers.value.first = (last ?? []).slice(0, last_block.total)
	}
}

const updateNumbersIncrementMultiple = () => {
	const { first_block, last_block } = getRulesLotterySelectMultiple.value
	const { first, last } = lotteryNumbers.value || { first: [], last: null }

	const countFirstBlock = lotteryNumbers.value?.first?.length || 0
	const countLastBlock = lotteryNumbers.value?.last?.length || 0

	const minCountFirstBlock = first_block.min_select + 1 || 0
	if (lotteryNumbers.value && countFirstBlock < minCountFirstBlock) {
		const difference = minCountFirstBlock - countFirstBlock
		const newNumbers = Array(difference).fill(null)
		lotteryNumbers.value.first = [...first, ...newNumbers]
	}

	if (last === null && last_block === null) return
	if (lotteryNumbers.value && countLastBlock < last_block.min_select) {
		const difference = last_block.min_select - countLastBlock
		const newNumbers = Array(difference).fill(null)
		lotteryNumbers.value.last = [...(last ?? []), ...newNumbers]
	}
}

const updateAmount = (value: number) => {
	amount.value = value
}

const changeMode = (mode: string) => {
	setNumbers()
	sendEvent()
	if (mode === 'multiple') {
		updateNumbersIncrementMultiple()
		return
	}
	updateNumbersReduced()
}

const toggleOpen = async () => {
	isOpen.value = !isOpen.value
}

let previousWidth = 0

const checkScreenSize = () => {
	const currentWidth = window.innerWidth

	if (currentWidth === previousWidth) return
	previousWidth = currentWidth
	const mobileMediaQuery = window.matchMedia('(max-width: 768px)')
	isMobile.value = mobileMediaQuery.matches
}

// fuera de funciones:
let prevHeight = 0

const onBeforeLeave = (el: Element) => {
	// guardamos altura del que se va (punto A)
	const elHtml = el as HTMLElement
	prevHeight = elHtml.getBoundingClientRect().height
}

const onBeforeEnter = (el: Element) => {
	// arrancamos en la ALTURA ANTERIOR para evitar el “shrink”
	const elHtml = el as HTMLElement
	elHtml.style.height = prevHeight + 'px'
	elHtml.style.opacity = '0'
	elHtml.style.transform = 'scale(0.995)'
}

const onEnter = (el: Element, done: () => void) => {
	// medimos la altura destino (punto B)
	const elHtml = el as HTMLElement
	const end = elHtml.scrollHeight
	elHtml.style.transition = 'height 320ms cubic-bezier(0.22,1,0.36,1), opacity 260ms ease, transform 260ms ease'
	// siguiente frame: animamos A -> B
	requestAnimationFrame(() => {
		elHtml.style.height = end + 'px'
		elHtml.style.opacity = '1'
		elHtml.style.transform = 'scale(1)'
	})
	// limpieza al terminar
	const cleanup = () => {
		elHtml.style.transition = ''
		elHtml.style.height = ''
		elHtml.style.opacity = ''
		elHtml.style.transform = ''
		elHtml.removeEventListener('transitionend', cleanup)
		done()
	}
	elHtml.addEventListener('transitionend', cleanup)
}

const onAfterEnter = (el: Element) => {
	// seguro extra por si el navegador no disparó el evento anterior
	const elHtml = el as HTMLElement
	elHtml.style.transition = ''
	elHtml.style.height = ''
	elHtml.style.opacity = ''
	elHtml.style.transform = ''
}

onMounted(() => {
	if (typeof window !== 'undefined') {
		setPropertysLottery()
		checkScreenSize()
		window.addEventListener('resize', checkScreenSize)
	}
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', checkScreenSize)
	resultStore.SET_STATE_RESULT({
		type: 'is_random',
		value: false,
	})
})
</script>

<template>
	<header
		id="heroResults"
		class="result-container bg-secondary-700 smm:bg-secondary-900 bg-auto lg:bg-cover bg-no-repeat bg-top"
	>
		<div class="container pt-12 pb-20 sm:pb-32 px-3 sm:px-auto">
			<div class="space-y-4 sm:space-y-6">
				<ScanTicketBanner />

				<div
					class="w-full rounded-[20px] relative z-10 bg-secondary-800 sm:bg-transparent"
					:class="{
						'bg-[linear-gradient(90deg,var(--secondary-600,#2E2948)_0.11%,#4C3D9C_99.71%)]': resultStore.isMutipleBets,
					}"
					@click="toggleOpen"
				>
					<BackgroundBanner
						v-if="resultStore.isMutipleBets === false"
						class="hidden sm:block"
					/>
					<Transition
						mode="out-in"
						@before-leave="onBeforeLeave"
						@before-enter="onBeforeEnter"
						@enter="onEnter"
						@after-enter="onAfterEnter"
					>
						<div :key="isOpen ? 'open' : 'closed'">
							<!-- Abierto -->
							<CardOpen
								v-if="isOpen"
								ref="cardOpenRef"
								:show-multiple-bets="isShowMultipleBets"
								:country="country"
								:countries="countries"
								:rules="getRulesLotterySelect || {}"
								:lottery="getLotterySelectItem"
								:lotteries="getLotteries || {}"
								:is-disabled-btn="isDisabledBtn"
								:random="random"
								:loader-btn-random="loaderSetRandomNumbers"
								:show-result="showResult"
								:block-numbers="lotteryNumbers"
								:amount="amount"
								:update-numbers="updateNumbers"
								:update-country="updateCountry"
								:update-lottery="updateLottery"
								@change-mode="changeMode"
								@update-amount="updateAmount"
							/>

							<!-- Cerrado -->
							<CardClosed
								v-else
								:country="country"
								:countries="countries"
								:show-multiple-bets="isShowMultipleBets"
								:lottery="getLotterySelectItem"
								:block-numbers="lotteryNumbers"
								:lotteries="getLotteries || {}"
								:update-lottery="updateLotteryIfClosed"
								:update-country="updateCountry"
								@change-mode="changeMode"
							/>
						</div>
					</Transition>
				</div>
			</div>
		</div>
	</header>
</template>

<style scoped>
.v-enter-from,
.v-leave-to {
	opacity: 0;
	transform: scale(0.995);
}
.v-enter-active,
.v-leave-active {
	transition:
		opacity 260ms ease,
		transform 260ms ease;
}

@media (min-width: 480px) {
	.result-container {
		background-image: url('https://d1p54ao86z4jzg.cloudfront.net/bubble_hero_result.webp');
	}
}
</style>
